import { useState } from "react";

// Third Party
import { createFileRoute } from "@tanstack/react-router";
import { Formik, Field } from "formik";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

// UI
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import TableSkeleton from "@/components/skeleton/TableSkeleton";
import { useToast } from "@/components/ui/use-toast";
import DatePicker from "@/components/ui/DatePicker";
import AlertModal from "@/components/AlertModal";

// Icons
import { PlusCircle, Pen, Trash } from "lucide-react";

export const Route = createFileRoute(
  "/_layout-private/account-management/soe/_information/secretariat/"
)({
  component: RouteComponent,
});

import {
  getSecretariat,
  updateSecretariat,
  deleteSecretariat,
  createSecretariat,
} from "@/services/soe";
import type { MutateSecretariat, SecretariatList } from "@/types/soe";
import formatDate from "@/utils/formatDate";
import { clearLocalStorage } from "@/helpers/accessLocalStorage";

function RouteComponent() {
  const id = localStorage.getItem("companyId");
  const companyId = id?.replace(/^"(.+)"$/, "$1") || "";
  const queryClient = useQueryClient();
  const [openModal, setOpenModal] = useState(false);

  const { toast } = useToast();

  const [selectedData, setSelectedData] = useState({} as SecretariatList);

  // Query
  const { data, isLoading, error } = useQuery({
    queryKey: ["secretariats", companyId],
    queryFn: () =>
      getSecretariat({
        skip: 0,
        companyId: companyId,
        limit: 100,
      }),
  });

  if (error) {
    const isAxiosError = (
      error: unknown
    ): error is { response?: { data?: { message?: string } } } => {
      return typeof error === "object" && error !== null && "response" in error;
    };

    if (
      isAxiosError(error) &&
      error?.response?.data?.message === "LOGGED_OUT"
    ) {
      console.log("clearing storage due to logged out");
      clearLocalStorage();
      window.location.reload();
    }
  }

  // Mutation
  const { mutate: createSecretariatMutation } = useMutation({
    mutationFn: (data: MutateSecretariat) => createSecretariat(data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["secretariats", companyId],
      });
      toast({
        title: "ສຳເລັດ",
        description: "ສ້າງຂໍ້ມູນກອງເລຂາສຳເລັດ",
      });
      handleClosePopup();
    },
    onError: () => {
      toast({
        title: "ຫຼົ້ມເຫຼວ",
        description: "ມີບາງຢ່າງຜິດພາດ ກະລຸນາລອງໃໝ່ອີກຄັ້ງ",
        variant: "destructive",
      });
    },
  });

  const { mutate: updateSecretariatMutation } = useMutation({
    mutationFn: (data: MutateSecretariat) =>
      updateSecretariat(selectedData?.id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["secretariats", companyId],
      });
      toast({
        title: "ສຳເລັດ",
        description: "ແກ້ໄຂຂໍ້ມູນກອງເລຂາສຳເລັດ",
      });

      handleClosePopup();
    },
    onError: () => {
      toast({
        title: "ຫຼົ້ມເຫຼວ",
        description: "ມີບາງຢ່າງຜິດພາດ ກະລຸນາລອງໃໝ່ອີກຄັ້ງ",
        variant: "destructive",
      });
    },
  });

  const { mutate: deleteSecretariatMutation } = useMutation({
    mutationFn: (id: string) => deleteSecretariat(id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["secretariats", companyId],
      });
      toast({
        title: "ລຶບບັນຊີສຳເລັດ",
        description: "ລຶບບັນຊີກອງເລຂາສຳເລັດ",
      });
    },
    onError: () => {
      toast({
        title: "ຫຼົ້ມເຫຼວ",
        description: "ມີບາງຢ່າງຜີດພາດ ກະລຸນາລອງໃໝ່ອີກຄັ້ງ",
        variant: "destructive",
      });
    },
  });

  // Functions

  const handleClosePopup = () => {
    setOpenModal(false);
    setSelectedData({} as SecretariatList);
  };

  const handleOnSubmit = (values: MutateSecretariat) => {
    if (selectedData?.id) {
      updateSecretariatMutation(values);
    } else {
      createSecretariatMutation(values);
    }
  };

  // Data
  const secretariats = data?.data as SecretariatList[];

  return (
    <div className="space-y-[32px]">
      <div className="text-[18px]">
        <h2 className="text-zinc-950">ຂໍ້ມູນກອງເລຂາ</h2>
        <p className="text-zinc-500">ຕາຕະລາງຂໍ້ມູນບັນຊີກອງເລຂາ</p>

        <Separator className="my-6" />
      </div>

      <div className="flex justify-end w-full">
        <Button
          onClick={() => {
            setOpenModal(true);
            setSelectedData({} as SecretariatList);
          }}
        >
          <PlusCircle className="mr-2" />
          ເພີ່ມຂໍ້ມູນເລຂາ
        </Button>
      </div>

      {isLoading ? (
        <TableSkeleton />
      ) : (
        <Table className="border rounded-md ">
          <TableHeader className="">
            <TableRow>
              <TableHead className="whitespace-nowrap">ລຳດັບ</TableHead>
              <TableHead className="whitespace-nowrap">
                ຊື່ ແລະ ນາມສະກຸນ
              </TableHead>
              <TableHead className="whitespace-nowrap">ຕຳແໜ່ງ</TableHead>
              <TableHead className="whitespace-nowrap">ບ່ອນສັງກັດ</TableHead>
              <TableHead className="whitespace-nowrap">ເບີໂທ</TableHead>

              <TableHead className="whitespace-nowrap">ຈັດການ</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {secretariats?.map((item, index) => (
              <TableRow key={item.id}>
                <TableCell>{index + 1}</TableCell>
                <TableCell>
                  {item.firstName} {item.lastName}
                </TableCell>
                <TableCell>{item.position}</TableCell>
                <TableCell>{item.affiliation}</TableCell>
                <TableCell>{item.phone}</TableCell>
                <TableCell className="space-x-2">
                  <Button
                    variant={"outline"}
                    size={"icon"}
                    onClick={() => {
                      setSelectedData(item);
                      setOpenModal(true);
                    }}
                  >
                    <Pen />
                  </Button>
                  <AlertModal
                    title="ລຶບບັນຊີເລຂາ"
                    desc="ທ່ານຕ້ອງການລຶບບັນຊີເລຂານີ້ແທ້ ຫຼື ບໍ່?"
                    triggerComponent={
                      <Button variant={"outline"} size={"icon"}>
                        <Trash />
                      </Button>
                    }
                    confirmFunction={() => deleteSecretariatMutation(item.id)}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <Dialog open={openModal} onOpenChange={handleClosePopup}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>ເພີ່ມຂໍ້ມູນກອງເລຂາ</DialogTitle>
          </DialogHeader>
          <Formik
            initialValues={{
              companyId: companyId,
              firstName: selectedData?.firstName || "",
              lastName: selectedData?.lastName || "",
              phone: selectedData?.phone || "",
              affiliation: selectedData?.affiliation || "",
              position: selectedData?.position || "",
              assignedDate:
                formatDate({
                  date: selectedData?.assignedDate || "",
                  format: "YYYY-MM-DD",
                }) || "",
            }}
            onSubmit={(values: MutateSecretariat) => {
              console.log(values);

              handleOnSubmit(values);
            }}
          >
            {({ handleSubmit, values, setFieldValue }) => (
              <form onSubmit={handleSubmit} className="space-y-[16px]">
                <div>
                  <Label>
                    ຊື່ <span className="text-red-500">*</span>
                  </Label>
                  <Field
                    as={Input}
                    name="firstName"
                    required
                    placeholder="ປ້ອນຊື່..."
                  />
                </div>
                <div>
                  <Label>
                    ນາມສະກຸນ <span className="text-red-500">*</span>
                  </Label>
                  <Field
                    as={Input}
                    name="lastName"
                    required
                    placeholder="ປ້ອນນາມສະກຸນ..."
                  />
                </div>

                <DatePicker
                  name="assignedDate"
                  label="ວັນທີແຕ່ງຕັ້ງ"
                  required
                  value={values.assignedDate}
                  onChange={(date) => {
                    setFieldValue("assignedDate", date);
                  }}
                />
                <div>
                  <Label>
                    ຕຳແໜ່ງ <span className="text-red-500">*</span>
                  </Label>
                  <Field
                    as={Input}
                    name="position"
                    required
                    placeholder="ປ້ອນຕຳແໜ່ງ..."
                  />
                </div>
                <div>
                  <Label>
                    ບ່ອນສັງກັດ <span className="text-red-500">*</span>
                  </Label>
                  <Field
                    as={Input}
                    name="affiliation"
                    required
                    placeholder="ປ້ອນບ່ອນສັງກັດ..."
                  />
                </div>
                <div>
                  <Label>
                    ເບີໂທລະສັບ <span className="text-red-500">*</span>
                  </Label>
                  <Field
                    as={Input}
                    name="phone"
                    required
                    placeholder="ປ້ອນເບີໂທລະສັບ..."
                  />
                </div>

                <div className="flex items-center justify-center w-full gap-4">
                  <Button
                    variant={"outline"}
                    onClick={() => setOpenModal(false)}
                  >
                    ຍົກເລີກ
                  </Button>
                  <Button type="submit">
                    {selectedData?.id ? "ແກ້ໄຂ" : "ບັນທຶກ"}
                  </Button>
                </div>
              </form>
            )}
          </Formik>
        </DialogContent>
      </Dialog>
    </div>
  );
}
